/**
 * <pa-projector>: fa ruotare le immagini proiettate nella scena dell'hero.
 * - Se il dispositivo lo permette, carica in differita la scena WebGL (pa-hero-3d.js);
 *   altrimenti resta la versione CSS, che è anche il ripiego in caso di errore.
 * - Si ferma fuori schermo o con la scheda nascosta; nessuna rotazione con prefers-reduced-motion.
 */
/** Risolve quando l'intro della homepage ha passato il testimone (o subito se non c'è). */
const introDone = () =>
  document.documentElement.classList.contains('pa-intro')
    ? new Promise((resolve) => document.addEventListener('pa:intro-landed', resolve, { once: true }))
    : Promise.resolve();

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function canUse3D() {
  if (reducedMotion() || navigator.connection?.saveData) return false;
  try {
    return Boolean(document.createElement('canvas').getContext('webgl2'));
  } catch {
    return false;
  }
}

class PaProjector extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.pa-stage__slide')];
    this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
    this.interval = Math.max(2000, Number(this.dataset.interval) || 3500);
    this.visible = true;
    this.paused = false;

    document.addEventListener('shopify:block:select', (this.onBlockSelect = this.handleBlockSelect.bind(this)));
    document.addEventListener('shopify:block:deselect', (this.onBlockDeselect = this.handleBlockDeselect.bind(this)));

    if (this.dataset.module && canUse3D()) this.mount3D();

    if (this.slides.length < 2 || reducedMotion()) return;

    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.schedule();
    });
    this.observer.observe(this);
    document.addEventListener('visibilitychange', (this.onVisibility = () => this.schedule()));

    // Il primo cambio aspetta l'intro e la sequenza d'accensione (~2.4 s).
    introDone().then(() => this.schedule(this.interval + 2400));
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    clearTimeout(this.swapTimer);
    clearTimeout(this.fallbackTimer);
    this.observer?.disconnect();
    this.scene3d?.destroy();
    this.scene3d = null;
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  /** Descrive le slide per la scena 3D (SVG inline o immagine caricata). */
  describeSlides() {
    return this.slides.map((slide) => {
      const svg = slide.querySelector('svg');
      if (svg) return { type: 'svg', markup: svg.outerHTML };
      const img = slide.querySelector('img');
      return { type: 'img', src: img?.currentSrc || img?.src, whiten: slide.classList.contains('pa-stage__slide--light') };
    });
  }

  async mount3D() {
    // La scena CSS resta in pausa finché la 3D non è pronta; dopo 3 s senza successo riparte.
    this.classList.add('is-3d-pending');
    this.fallbackTimer = setTimeout(() => this.classList.remove('is-3d-pending'), 3000);
    try {
      const { mount } = await import(this.dataset.module);
      if (!this.isConnected) return;
      this.scene3d = await mount(this, this.describeSlides(), { variant: this.dataset.variant });
      if (!this.isConnected) return this.scene3d.destroy();
      this.scene3d.show(this.index);
      clearTimeout(this.fallbackTimer);
      this.classList.remove('is-3d-pending');
      this.classList.add('is-3d');
      // La scena parte spenta (come il poster) e si accende lampione dopo lampione:
      // con l'intro all'atterraggio del logo, senza intro subito.
      await introDone();
      if (this.isConnected) setTimeout(() => this.scene3d?.ignite(), 150);
    } catch (error) {
      console.warn('[pa-projector] scena 3D non disponibile, uso la versione CSS.', error);
      clearTimeout(this.fallbackTimer);
      this.classList.remove('is-3d-pending');
    }
  }

  schedule(delay = this.interval) {
    clearTimeout(this.timer);
    if (!this.visible || document.hidden || this.paused || this.slides.length < 2) return;
    this.timer = setTimeout(() => this.show((this.index + 1) % this.slides.length), delay);
  }

  show(next) {
    if (next === this.index) return;
    this.slides[this.index].classList.remove('is-active');
    this.classList.add('is-switching');
    this.index = next;
    this.scene3d?.show(next);
    // Breve "spegnimento" del fascio tra un'immagine e l'altra.
    this.swapTimer = setTimeout(() => {
      this.slides[this.index].classList.add('is-active');
      this.classList.remove('is-switching');
      this.schedule();
    }, 450);
  }

  // Nell'editor del tema: mostra la slide selezionata e ferma la rotazione.
  handleBlockSelect(event) {
    const i = this.slides.findIndex((s) => s.contains(event.target) || s === event.target);
    if (i < 0) return;
    this.paused = true;
    clearTimeout(this.timer);
    this.show(i);
  }

  handleBlockDeselect() {
    this.paused = false;
    this.schedule();
  }
}

if (!customElements.get('pa-projector')) customElements.define('pa-projector', PaProjector);
